import { Button as MuiButton, DialogActions, DialogContent, Grid, styled, Typography } from '@mui/material';
import { spacing } from '@mui/system';
import React, { useCallback, ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';

import { AddressLookupStatus } from '../../../../domain/address';
import { AddressView } from '../../../../general/components/address-view';
import { Dialog } from '../../../../general/components/dialog';
import { RootState } from '../../../../store';

const selector = ({ addressLookup: { enteredAddress } }: RootState) => enteredAddress;

const Button = styled(MuiButton)(spacing);
type UnverifiedStatus = Extract<
  AddressLookupStatus,
  'not_verified' | 'partial_unverified_secondary' | 'partial_missing_secondary'
>;

const config: Record<UnverifiedStatus, { title: React.ReactNode; button: string; subheading: string | null }> = {
  not_verified: {
    title: "We couldn't verify your address. Are you sure it's correct?",
    button: " Yes, I'm sure",
    subheading: null,
  },
  partial_missing_secondary: {
    title: (
      <>
        The address you entered might be missing a room or suite number.
        <br /> Are you sure it&apos;s correct?
      </>
    ),
    button: 'Use what I entered',
    subheading: 'What you entered:',
  },
  partial_unverified_secondary: {
    title: (
      <>
        We were unable to verify the room or suite number you provided.
        <br /> Are you sure it&apos;s correct?
      </>
    ),
    button: 'Use what I entered',
    subheading: 'What you entered:',
  },
};

type Props = {
  onClose: () => void;
  context: {
    next: Action;
    status: UnverifiedStatus;
  };
};

const UnverifiedAddressDlg: React.FC<Props> = ({
  onClose,
  context: { status, next: nextAction },
}: Props): ReactElement => {
  const address = useSelector(selector);
  const dispatch = useDispatch();
  const handleNext = useCallback(() => {
    dispatch(nextAction);
    onClose();
  }, [nextAction, dispatch, onClose]);

  const { title, button, subheading } = config[status];

  return (
    <Dialog title={title} onClose={onClose} fullWidth>
      <DialogContent dividers>
        {subheading && <Typography variant="body2">{subheading}</Typography>}
        <Typography variant="body2" color="textSecondary" component="div">
          <AddressView address={address!} />
        </Typography>
      </DialogContent>

      <DialogActions>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} display="flex">
            <Button color="primary" size="large" onClick={handleNext} sx={{ flexGrow: { xs: 1, sm: 0 } }}>
              {button}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} display="flex">
            <Button
              color="primary"
              size="large"
              onClick={onClose}
              variant="contained"
              sx={{ flexGrow: { xs: 1, sm: 0 }, ml: { sm: 'auto' } }}
            >
              Change Address
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export { UnverifiedAddressDlg };
